import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
// testing
export interface EConsentStatusRequest {
  user_id: number;
  fi_id: number;
  external_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface EConsentStatusApiResponse {
  submitted: boolean;
  consent_state?: number;
  consentState?: number;
  verification_filename?: string | null;
  verificationFilename?: string | null;
  prompt_action_label?: string | null;
  promptActionLabel?: string | null;
  description?: string | null;
  confirm_label?: string | null;
  confirmLabel?: string | null;
}

export interface EConsentStatusResponse {
  submitted: boolean;
  consentState: number | null;
  verificationFilename: string | null;
  promptActionLabel: string;
  description: string;
  confirmLabel: string;
}

export interface SubmitEConsentRequest {
  userId: number;
  body: {
    accept: boolean;
    decline: boolean;
    validation_code?: string;
    verification_filename?: string;
  };
}

interface SubmitEConsentApiResponse {
  success: boolean;
  consent_state?: number;
  consentState?: number;
  verification_filename?: string | null;
  verificationFilename?: string | null;
}

export interface SubmitEConsentResponse {
  success: boolean;
  consentState: number | null;
  verificationFilename: string | null;
}

export interface MarkEConsentUserIgnoredRequest {
  userId: number;
}

interface MarkEConsentUserIgnoredApiResponse {
  success: boolean;
  consent_state?: number;
  consentState?: number;
}

export interface MarkEConsentUserIgnoredResponse {
  success: boolean;
  consentState: number | null;
}

export const eConsentApi = createApi({
  reducerPath: 'eConsentApi',
  baseQuery: waServicesQuery,
  endpoints: (builder) => ({
    getEConsentStatus: builder.mutation<
      EConsentStatusResponse,
      EConsentStatusRequest
    >({
      query: (body) => ({
        url: '/v1/consent',
        method: 'POST',
        body,
      }),
      transformResponse: (
        response: EConsentStatusApiResponse,
      ): EConsentStatusResponse => ({
        submitted: response.submitted,
        consentState: response.consent_state ?? response.consentState ?? null,
        verificationFilename:
          response.verification_filename ??
          response.verificationFilename ??
          null,
        promptActionLabel:
          response.prompt_action_label ?? response.promptActionLabel ?? '',
        description: response.description ?? '',
        confirmLabel: response.confirm_label ?? response.confirmLabel ?? '',
      }),
    }),
    submitEConsent: builder.mutation<
      SubmitEConsentResponse,
      SubmitEConsentRequest
    >({
      query: ({ userId, body }) => ({
        url: `/v1/consent/${userId}`,
        method: 'POST',
        body,
      }),
      transformResponse: (
        response: SubmitEConsentApiResponse,
      ): SubmitEConsentResponse => ({
        success: response.success,
        consentState: response.consent_state ?? response.consentState ?? null,
        verificationFilename:
          response.verification_filename ??
          response.verificationFilename ??
          null,
      }),
    }),
    markEConsentUserIgnored: builder.mutation<
      MarkEConsentUserIgnoredResponse,
      MarkEConsentUserIgnoredRequest
    >({
      query: ({ userId }) => ({
        url: `/v1/consent/${userId}/user-ignored`,
        method: 'POST',
      }),
      transformResponse: (
        response: MarkEConsentUserIgnoredApiResponse,
      ): MarkEConsentUserIgnoredResponse => ({
        success: response.success,
        consentState: response.consent_state ?? response.consentState ?? null,
      }),
    }),
  }),
});

export const {
  useGetEConsentStatusMutation,
  useSubmitEConsentMutation,
  useMarkEConsentUserIgnoredMutation,
} = eConsentApi;
